import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PasConfirmationService,
  PasLayoutSidebarHolderComponent,
  PasSidebarHandleService,
  PasTabContentDirective,
  PasTabDirective,
  PasTabsetComponent,
  PasTabTitleDirective,
} from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { RowNode } from 'ag-grid-enterprise';
import { Observable } from 'rxjs';

import { RecourseBookingDto } from '../../api/tur-rc/models';
import { RecourseBookingService } from '../../api/tur-rc/services';
import { AutomatedAftersalesBookingsApprovalPipe } from '../../pipes/automated-aftersales-bookings-approval.pipe';
import { AutomatedRecourseBookingsApprovalPipe } from '../../pipes/automated-recourse-bookings-approval.pipe';
import { AGGridExcelService } from '../../services/ag-grid/excel-service';
import { AGGridFormatterService } from '../../services/ag-grid/formatter-service';
import { NotificationServiceWrapper } from '../../services/notification-service-wrapper/notification-service-wrapper';
import { UserService } from '../../services/user-service/user.service';
import { AG_DATE_COL_CONFIG } from '../../shared/ag-grid/ag-grid-functions';
import { AgGridService } from '../../shared/ag-grid/ag-grid.service';
import { ApprovalButtonCellRenderer } from '../../shared/ag-grid/approval-button-cell-renderer/approval-button-cell-renderer';
import { ApproveRejectReprocessDialog } from '../../shared/approve-reject-reprocess-dialog/approve-reject-reprocess-dialog.component';
import { RecourseTypeIdEnum } from '../../shared/enums/recourse-type-id-enum';
import { TableViewAdministrationState } from '../../sub-component/table-view-administration/common/enum/table-view-administration-state.enum';
import { TvaSessionSidebarComponent } from '../../sub-component/table-view-administration/tva-session/component-sidebar/tva-session-sidebar.component';
import { TvaSessionService } from '../../sub-component/table-view-administration/tva-session/tva-session.service';

@Component({
  selector: 'app-tur-approval',
  styleUrls: ['./approval.component.scss'],
  templateUrl: './approval.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PasTabsetComponent,
    PasTabDirective,
    TranslatePipe,
    AgGridAngular,
    PasLayoutSidebarHolderComponent,
    AutomatedAftersalesBookingsApprovalPipe,
    AutomatedRecourseBookingsApprovalPipe,
    NgClass,
    TvaSessionSidebarComponent,
    ApproveRejectReprocessDialog,
    PasTabTitleDirective,
    PasTabContentDirective,
  ],
})
export class ApprovalComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly agGridService = inject(AgGridService);
  private readonly bookingService = inject(RecourseBookingService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly agGridExcelService = inject(AGGridExcelService);
  private readonly userService = inject(UserService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly tvaSessionService = inject(TvaSessionService);

  public columnDefs: any;
  public rowData: any;
  public defaultColDef: any;
  public multiSortKey: any;
  public gridApi: any;
  public gridOptions: GridOptions;
  public frameworkComponents: any;
  public isCreateExcelApprovalEnabled = true;
  public excelStyles: any;
  public tooltipShowDelay = 0;

  public approvalsOverviewData: any[] = [];
  private approvedBookings: any[] = [];
  private tmpParam: any;
  protected aftersalesBookingsToApprove = 0;
  protected recourseBookingsToApprove = 0;

  private readonly automatedSystemBooking = '0';

  public ngOnInit(): void {
    this.multiSortKey = 'ctrl';

    this.gridOptions = {
      rowHeight: 35,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      suppressCellFocus: true,
      context: { componentParent: this },
      getLocaleText: this.agGridService.translate(this.userService, this.translateService),
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };

    this.defaultColDef = {
      editable: false,
      resizable: true,
      sortable: true,
      floatingFilter: true,
      unSortIcon: true,
      filter: true,
    };

    this.translateService.get('APPROVAL_TAB').subscribe(() => {
      this.columnDefs = [
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_BOOKING_ID'),
          field: 'pkBookingId',
          sort: 'asc',
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_BOOKING_ID'),
          filter: 'agNumberColumnFilter',
          cellStyle: { textAlign: 'right' },
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_SUPPLIER'),
          field: 'supplierData.name',
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_SUPPLIER'),
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_SUPPLIER_ID'),
          field: 'supplierData.supplier',
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_SUPPLIER_ID'),
          cellStyle: { textAlign: 'right' },
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_DEBITOR_ID'),
          field: 'supplierData.debitor',
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_DEBITOR_ID'),
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_BOOKING_TYPE'),
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_BOOKING_TYPE'),
          valueGetter: this.agGridService.translateBookingType(this.translateService),
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressMiniFilter: true,
          },
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_AMOUNT'),
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_AMOUNT'),
          cellStyle: { textAlign: 'right' },
          filter: false,
          valueGetter: this.agGridService.getEurAmountWithBookingTypeCheck(),
          cellClass: this.agGridFormatterService.cellClassCurrencyType,
          valueFormatter: this.agGridFormatterService.currencyFormatter(this.userService),
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_RECOURSE_TYPE'),
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_RECOURSE_TYPE'),
          valueGetter: this.agGridService.getRecourseType(this.translateService),
          filter: 'agSetColumnFilter',
          menuTabs: ['generalMenuTab', 'filterMenuTab'],
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_CREATE_DATE'),
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_CREATE_DATE'),
          ...AG_DATE_COL_CONFIG,
          field: 'createDate',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          minWidth: 155,
          cellStyle: { textAlign: 'right' },
          cellClass: 'dateFormat',
        },
        {
          headerName: this.translateService.instant('RECOURSE_BOOKING_TABLE_CREATION_TYPE'),
          headerTooltip: this.translateService.instant('RECOURSE_BOOKING_TABLE_TOOLTIP_CREATION_TYPE'),
          filter: 'agSetColumnFilter',
          minWidth: 50,
          valueGetter: this.agGridService.convertCreationType(),
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_CREATOR'),
          field: 'originator',
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_CREATOR'),
          filter: 'agSetColumnFilter',
        },
        {
          headerName: this.translateService.instant('APPROVAL_TABLE_INVOICE_REASON'),
          field: 'invoiceReasonText',
          headerTooltip: this.translateService.instant('APPROVAL_TABLE_TOOLTIP_INVOICE_REASON'),
          filter: 'agTextColumnFilter',
        },
        {
          colId: 'buttonColumn',
          headerName: '',
          headerClass: ['ag-header-last-cell'],
          field: 'value',
          cellRenderer: ApprovalButtonCellRenderer,
          menuTabs: [],
          unSortIcon: false,
          minWidth: 130,
          filter: false,
        },
      ];
    });
    this.excelStyles = this.agGridExcelService.getExcelStyles(this.translateService);
  }

  public recalculateAutomaticBookings(): void {
    const renderedRowData: RecourseBookingDto[] = [];
    this.gridApi.forEachNodeAfterFilterAndSort((node: RowNode) => {
      if (node.rowIndex !== null) {
        renderedRowData.push(node.data);
      }
    });
    this.calculateAutomaticBookings(renderedRowData);
  }

  private calculateAutomaticBookings(bookingPendingApprovals: RecourseBookingDto[]): void {
    this.aftersalesBookingsToApprove = 0;
    this.recourseBookingsToApprove = 0;
    bookingPendingApprovals.forEach(element => {
      if (this.automatedSystemBooking === element.manualBooking) {
        if (RecourseTypeIdEnum.AFTERSALES == element.recourseTypeId) {
          this.aftersalesBookingsToApprove += 1;
        }
        if (RecourseTypeIdEnum.RECOURSE == element.recourseTypeId) {
          this.recourseBookingsToApprove += 1;
        }
      }
    });
  }

  public onGridReady(event: GridReadyEvent): void {
    this.sidebarHandleService.enableSidebarForView();
    this.tmpParam = event;
    this.gridApi = event.api;
    this.gridApi.updateGridOptions({
      headerHeight: 32,
    });

    this.bookingService.getRecourseBookingPendingApprovals().subscribe(bookingPendingApprovals => {
      this.approvalsOverviewData = bookingPendingApprovals;
      this.calculateAutomaticBookings(bookingPendingApprovals);
      this.rowData = bookingPendingApprovals;

      this.tvaSessionService.init(TableViewAdministrationState.Approval, false, this.gridOptions, null, this.gridApi);
    });
  }

  public getContextMenuItems(): [] {
    return [];
  }

  public onBtnCreateExcelApproval(): void {
    try {
      this.createExcelApproval();
    } catch {
      const title = this.translateService.instant('EXCEL_EXPORT_ERROR_TITLE');
      const content = this.translateService.instant('EXCEL_EXPORT_ERROR_CONTENT');
      this.notificationServiceWrapper.addErrorNotification(title, content);
    }
  }

  public createExcelApproval(): void {
    this.gridApi.exportDataAsExcel(
      this.agGridExcelService.getExcelFrame(
        this.translateService.instant('APPROVAL_TAB'),
        this.translateService.instant('EXCEL_EXPORT_APPROVAL_OVERVIEW'),
        this.gridOptions,
      ),
    );
  }

  public approveAllAutomatedAftersalesBookings(): void {
    const title = this.translateService.instant('APPROVE_AUTOMATED_AFTERSALES_BOOKINGS');
    const recourseType = this.translateService.instant('AFTERSALES');
    const method: Observable<any> = this.bookingService.startApprovalForAftersalesBookings();

    this.approveAllAutomatedBookings(title, recourseType, this.aftersalesBookingsToApprove, method);
  }

  public approveAllAutomatedRecourseBookings(): void {
    const title = this.translateService.instant('APPROVE_AUTOMATED_RECOURSE_BOOKINGS');
    const recourseType = this.translateService.instant('RECOURSE');
    const method: Observable<any> = this.bookingService.approveAllAutomatedRecourseBookings();

    this.approveAllAutomatedBookings(title, recourseType, this.recourseBookingsToApprove, method);
  }

  public approveAllAutomatedBookings(
    title: string,
    recourseType: string,
    totalBookingsToApprove: number,
    method: Observable<any>,
  ): void {
    this.translateService
      .get(['APPROVE_AUTOMATED_BOOKINGS_DIALOG_MESSAGE', 'OK', 'CANCEL'])
      .subscribe((translate: { [key: string]: any }) => {
        const message: string = this.translateService.instant('APPROVE_AUTOMATED_BOOKINGS_DIALOG_MESSAGE', {
          0: totalBookingsToApprove,
          1: recourseType,
        });

        this.pasConfirmationService.showConfirmation(title, message, translate.OK, translate.CANCEL, false).then(
          () => {
            method.subscribe({
              next: listOfBookingIds => {
                this.approvedBookings = listOfBookingIds;
                this.onGridReady(this.tmpParam);
              },
              error: () => {
                this.showBtnApproveAllFailedMessage(totalBookingsToApprove);
              },
              complete: () => {
                this.showBtnApprovedAllSuccessMessage();
                // eslint-disable-next-line no-restricted-globals
                setTimeout(() => {}, 500);
              },
            });
          },
          () => {},
        );
      });
  }

  private showBtnApprovedAllSuccessMessage(): void {
    this.showSuccessMessage(
      this.approvedBookings.length,
      'APPROVE_BOOKING_TITLE',
      'APPROVE_AUTOMATED_BOOKINGS_SUCCESS_MESSAGE',
    );
  }

  private showSuccessMessage(numberOfBookings: any, messageTitle: string, messageContent: string): void {
    const title = this.translateService.instant(messageTitle);
    const content = this.translateService.instant(messageContent, { 0: numberOfBookings });
    this.notificationServiceWrapper.addSuccessNotification(title, content);
  }

  private showBtnApproveAllFailedMessage(totalBookingsToApprove: number): void {
    this.showFailureMessage(
      totalBookingsToApprove,
      'APPROVE_BOOKING_FAILED_TITLE',
      'APPROVE_AUTOMATED_BOOKINGS_FAILED_MESSAGE',
    );
  }

  private showFailureMessage(numberOfBookings: any, messageTitle: string, messageContent: string): void {
    const title = this.translateService.instant(messageTitle);
    const content = this.translateService.instant(messageContent, { 0: numberOfBookings });
    this.notificationServiceWrapper.addErrorNotification(title, content);
  }
}
